import React from "react"
import { TestTimeIndicator } from "../../services/utils/practice-test-util"
import { SprintsRest } from "../../services/sprints/sprints-rest-interface"
import ComputerTestACT, { ACTSessionAdapter } from "../Practice/VirtualPractice/ACT/ComputerTestACT"
import { CDN_PUB_URL_SPRINTS } from "../../constants"
import { SimpleTimer } from "../../services/utils/simple-timer"
import { Context, Resource, sendUserActivity, UserActivityEx } from "../../services/liveview/liveview-service"

interface Props {
    timedSprintId: string
    testTimeIndicator: TestTimeIndicator
    userInfo: any
    onNext: () => void
    onTestExit: () => void
}

export function adaptSprint(fullSprintInfo: any, testTimeIndicator: TestTimeIndicator) {
    let {timedSprint, userTimedSprint} = fullSprintInfo
    let subject = timedSprint.subject.toLowerCase()
    let timedSprintId = timedSprint.id
    if (!["english", "math", "reading", "science"].includes(subject) || !timedSprint.questions.length || !userTimedSprint || !Number.isFinite(userTimedSprint.remainingTimeInSeconds) || userTimedSprint.remainingTimeInSeconds < 0) {
        throw new Error("Sprint data is incomplete.")
    }
    let moduleKey = `${subject}Answers`
    let questions = timedSprint.questions.map(question => {
        let answer = (userTimedSprint.answers || []).find(a => a.questionNumber === question.questionNumber)
        let adapted = {
            ...question,
            section: moduleKey,
            subjectName: timedSprint.subject,
            mcLetters: Array.isArray(question.mcLetters) ? question.mcLetters : (question.mcLetters.match(/[A-Za-z]/g) || []).map(letter => letter.toUpperCase()),
            choiceA: question.choice1,
            choiceB: question.choice2,
            choiceC: question.choice3,
            choiceD: question.choice4,
            choiceE: question.choice5,
            userSubmittedAnswer: answer && answer.userSubmittedAnswer !== "?" ? answer.userSubmittedAnswer : "",
            bookmarked: !!(answer && answer.bookmarked),
        }
        return adapted
    })
    questions.forEach(question => {
        if (!question.questionGroup) return
        let group = questions.find(q => q.questionGroup === question.questionGroup && q.questionBox)
        if (group) {
            question.questionBox = question.questionBox || group.questionBox
            question.questionText = question.questionText || group.questionText
        }
    })
    return {
        timedSprintId,
        subject,
        displayName: `Sprint ${timedSprint.sprintNumber}`,
        questions: {[moduleKey]: questions},
        currentModuleNameKey: moduleKey,
        currentQuestionIndex: Math.max(0, Math.min(userTimedSprint.currentQuestionIndex || 0, questions.length - 1)),
        remainingTimeInSeconds: userTimedSprint.remainingTimeInSeconds,
        testTimeIndicator: userTimedSprint.testTimeIndicator || testTimeIndicator,
    }
}

interface State { 
    testDetails?: ReturnType<typeof adaptSprint>
    error: string 
}

export default class TimedSprintACT extends React.Component<Props, State> {
    state: State = {error: ""}
    mounted = false
    pending: Array<() => Promise<unknown>> = []
    saving?: Promise<void>
    studyTimer = new SimpleTimer()

    componentDidMount() { this.mounted = true; this.load() }
    componentWillUnmount() { 
        this.mounted = false 
        this.sendStats()
    }

    sendStats = () => {
        let {testDetails} = this.state

        if (!testDetails) {
            console.log("No testDetails found when unmounting. Cannot send stats!")
            return
        }

        let {timedSprintId, subject} = testDetails
        let seconds = this.studyTimer.diffInSeconds()
        let ended = seconds
        let context = Context.TIMEDSPRINT
        let contextId = "TimedSprintTest"
        let category = subject
        let resource = Resource.TEST_SESSION
        let resourceId = timedSprintId
        let userActivityEx: UserActivityEx = {
            context, contextId, 
            resource, resourceId, 
            category,
            ended
        }
        sendUserActivity(userActivityEx, true)
        this.studyTimer.reset()
    }

    load = async () => {
        this.setState({error: ""})
        try {
            let full = await SprintsRest.getFullSprintWithUserAnswers(this.props.timedSprintId)
            let testDetails = adaptSprint(full, this.props.testTimeIndicator)
            if (this.mounted) {
                this.studyTimer.start()
                this.setState({testDetails})
            }
        } catch (error) {
            if (this.mounted) this.setState({error: "Unable to load the sprint. Please try again."})
        }
    }

    // The backend writes the whole sprint record, so serialize answer and metadata updates.
    flush = (): Promise<void> => {
        if (this.saving) return this.saving
        this.saving = (async () => {
            while (this.pending.length) {
                await this.pending[0]()
                this.pending.shift()
            }
        })().then(() => {
            this.saving = undefined
            if (this.mounted) this.setState({error: ""})
        }, error => {
            this.saving = undefined
            if (this.mounted) this.setState({error: "Unable to save sprint progress. Please retry before leaving."})
            throw error
        })
        return this.saving
    }

    sessionAdapter: ACTSessionAdapter = {
        saveAnswer: (question, remainingTimeInSeconds) => {
            let update = {
                timedSprintId: this.props.timedSprintId,
                questionNumber: question.questionNumber,
                correctAnswer: question.correctAnswer,
                userSubmittedAnswer: question.userSubmittedAnswer || "?",
                bookmarked: !!question.bookmarked,
                studyTimeInSeconds: this.studyTimer.lapsedDiffInSeconds(),
                remainingTimeInSeconds,
            }
            this.pending.push(() => SprintsRest.saveAnswer(update))
            this.flush().catch(() => {})
        },
        saveProgress: async (currentQuestionIndex, remainingTimeInSeconds, completed) => {
            let update = {timedSprintId: this.props.timedSprintId, currentQuestionIndex, remainingTimeInSeconds: completed ? 0 : remainingTimeInSeconds}
            this.pending.push(() => SprintsRest.saveProgress(update))
            await this.flush()
        },
    }

    render() {
        let {testDetails, error} = this.state
        return <>
            {error && <div role="alert">{error} <button onClick={() => testDetails ? this.flush().catch(() => {}) : this.load()}>Retry</button></div>}
            {!testDetails && !error && <div role="status">Loading sprint…</div>}
            {testDetails && <ComputerTestACT
                baseUrlCDN={CDN_PUB_URL_SPRINTS}
                userInfo={this.props.userInfo}
                testDetails={testDetails}
                currentModuleNameKey={testDetails.currentModuleNameKey}
                sessionAdapter={this.sessionAdapter}
                onNext={this.props.onNext}
                onTestExit={this.props.onTestExit}
            />}
        </>
    }
}
